import { UNO_LABEL, type PlayCard } from "./deck";
import { ROLES, roleById } from "./identities";
import { COMMON_ROOM } from "./model";
import { PeerRoom } from "./PeerRoom";
import { Porthole } from "./Porthole";
import { preciseProfile, randomRoomCode, rollProfile, type ProfileDraft } from "./profile";
import { viewFor, ranked } from "./store";
import type { GameState } from "./rules";
import type { Profile } from "./model";

export class HqApp {
  private room: PeerRoom | null = null;
  private draft: ProfileDraft = rollProfile();
  private readonly thread = el("#thread");
  private readonly standings = el("#standings");
  private readonly crown = el("#crown-name");
  private readonly avatars = el("#avatars");
  private readonly score = el("#night-score");
  private readonly subtitle = el("#subtitle");
  private readonly gate = el("#gate");
  private readonly status = el("#net-status");
  private readonly preview = el("#profile-preview");
  private readonly narrator = el("#narrator");
  private readonly prompt = el("#prompt");
  private readonly felt = el("#felt");
  private readonly hand = el("#hand");
  private readonly dossier = el("#dossier");
  private readonly hint = el("#turn-hint");
  private readonly toast = el("#toast");
  private readonly magnet = el("#magnet");
  private readonly deal = el("#deal") as HTMLButtonElement;
  private readonly next = el("#next") as HTMLButtonElement;
  private readonly guide = el("#guide");
  private readonly autoSit = el("#auto-sit");
  private readonly draftInput = document.querySelector<HTMLInputElement>("#draft");
  private autoTimer = 0;
  private autoLeft = 10;
  private porthole: Porthole | null = null;

  start(): void {
    const canvas = document.querySelector<HTMLCanvasElement>("#porthole");
    if (canvas) {
      this.porthole = new Porthole(canvas);
      this.porthole.start();
    }

    const roomInput = document.querySelector<HTMLInputElement>("#room-code");
    if (roomInput) {
      const fromUrl = new URLSearchParams(location.search).get("room");
      roomInput.value = fromUrl || randomRoomCode();
    }

    this.paintDraft();
    this.gate.classList.remove("hidden");
    this.autoSit.innerHTML = `Auto-sitting as <strong>${escapeHtml(this.draft.name)}</strong> in <span id="auto-n">10</span>s · Surprise me cancels`;
    this.armAutoSit();

    el("#roll").addEventListener("click", () => {
      this.cancelAutoSit();
      this.draft = rollProfile();
      this.paintDraft();
    });
    el("#gate-form").addEventListener("input", () => {
      this.cancelAutoSit();
      this.draft = this.readForm();
      this.paintDraft();
    });
    el("#random-room").addEventListener("click", () => {
      this.cancelAutoSit();
      if (roomInput) roomInput.value = randomRoomCode();
    });
    el("#common-room").addEventListener("click", () => {
      this.cancelAutoSit();
      if (roomInput) roomInput.value = COMMON_ROOM;
    });
    el("#gate-form").addEventListener("submit", (event) => {
      event.preventDefault();
      this.cancelAutoSit();
      this.draft = this.readForm();
      this.join((roomInput?.value.trim() || COMMON_ROOM).toLowerCase());
    });
    el("#howto-open").addEventListener("click", () => this.openGuide());
    el("#guide-close").addEventListener("click", () => this.closeGuide());
    this.guide.addEventListener("click", (event) => {
      if (event.target === this.guide) this.closeGuide();
    });
    this.deal.addEventListener("click", () => this.room?.sendStart());
    this.next.addEventListener("click", () => this.room?.sendNext());
    el("#composer").addEventListener("submit", (event) => {
      event.preventDefault();
      const text = this.draftInput?.value.trim() ?? "";
      if (!text || !this.room) return;
      if (this.draftInput) this.draftInput.value = "";
      const fridgeItem = text.match(/^\/fridge\s+(.+)/i);
      if (fridgeItem?.[1]) this.room.sendFridge(fridgeItem[1]);
      else this.room.sendChat(text);
    });
    this.hand.addEventListener("click", (event) => {
      const btn = (event.target as HTMLElement).closest<HTMLElement>("[data-card]");
      if (!btn || !this.room) return;
      this.room.sendPlay(btn.dataset.card ?? "");
    });
    this.felt.addEventListener("click", (event) => {
      const btn = (event.target as HTMLElement).closest<HTMLElement>("[data-play]");
      if (!btn || !this.room) return;
      this.room.sendJudge(btn.dataset.play ?? "");
    });
    this.dossier.addEventListener("submit", (event) => {
      event.preventDefault();
      const form = event.target as HTMLFormElement;
      const target = String(new FormData(form).get("target") ?? "");
      const role = String(new FormData(form).get("role") ?? "");
      if (target && role) this.room?.sendGuess(target, role);
    });
  }

  dispose(): void {
    this.cancelAutoSit();
    this.room?.dispose();
    this.porthole?.dispose();
  }

  private armAutoSit(): void {
    window.clearTimeout(this.autoTimer);
    this.autoSit.hidden = false;
    this.autoLeft = 10;
    const n = document.querySelector("#auto-n");
    if (n) n.textContent = String(this.autoLeft);
    const tick = (): void => {
      this.autoLeft -= 1;
      const count = document.querySelector("#auto-n");
      if (count) count.textContent = String(this.autoLeft);
      if (this.autoLeft <= 0) {
        el("#gate-form").dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
        return;
      }
      this.autoTimer = window.setTimeout(tick, 1000);
    };
    this.autoTimer = window.setTimeout(tick, 1000);
  }

  private cancelAutoSit(): void {
    window.clearTimeout(this.autoTimer);
    this.autoSit.hidden = true;
  }

  private openGuide(): void {
    this.guide.hidden = false;
  }

  private closeGuide(): void {
    this.guide.hidden = true;
  }

  private join(room: string): void {
    this.room?.dispose();
    this.status.textContent = "saving you a seat…";
    this.room = new PeerRoom(room, this.draft, () => this.renderAll(), (s) => {
      this.status.textContent = s;
    });
    this.room.connect();
    this.gate.classList.add("hidden");
    this.gate.hidden = true;
    this.gate.setAttribute("aria-hidden", "true");
    const url = new URL(location.href);
    url.searchParams.set("room", room);
    history.replaceState({}, "", url);
  }

  private readForm(): ProfileDraft {
    return preciseProfile({
      name: val("#handle"),
      color: this.draft.color,
      creature: this.draft.creature,
      vibe: this.draft.vibe,
    });
  }

  private paintDraft(): void {
    this.preview.innerHTML = `<span class="dot" style="background:${this.draft.color}"></span>
      <strong>${escapeHtml(this.draft.name)}</strong>
      · ${escapeHtml(this.draft.vibe)}`;
    setVal("#handle", this.draft.name);
  }

  private renderAll(): void {
    const raw = this.room?.state;
    if (!raw || !this.room) return;
    const state = this.room.host ? viewFor(raw, this.room.selfId) : raw;
    const me = state.members.find((m) => m.id === this.room?.selfId);
    const game = state.game;
    const family = state.members.filter((m) => !m.left);
    this.subtitle.textContent = `${state.room} · ${family.length} weirdos`;
    this.score.textContent = `${me?.score ?? 0} pts`;
    document.querySelectorAll(".hud-score").forEach((node) => {
      node.textContent = `${me?.score ?? 0} pts`;
    });
    this.narrator.textContent = game.narration;
    this.avatars.innerHTML = family
      .map((m) => `<span title="${escapeHtml(m.name)}" style="background:${m.color}">${escapeHtml(m.initial)}</span>`)
      .join("");
    const board = ranked(state);
    this.crown.textContent = board[0] && board[0].score > 0 ? board[0].name : "nobody yet";
    this.standings.innerHTML = board.map((m) => `<li>${escapeHtml(m.name)} · ${m.score}</li>`).join("");

    if (game.phase === "lol" && game.punchline) {
      const win = game.table.find((p) => p.id === game.winPlayId);
      const answer = win?.card.kind === "white" ? win.card.text : "";
      this.prompt.innerHTML = `<article class="card black big lol-card">${formatPunchline(game.punchline, answer)}<span class="mark">LOL</span></article>`;
    } else {
      this.prompt.innerHTML = game.prompt
        ? `<article class="card black big">${formatPrompt(game.prompt.text)}<span class="mark">SETUP</span></article>`
        : `<article class="card black big"><p>Sit down. The blank is waiting.</p><span class="mark">FAMILY HQ</span></article>`;
    }

    if (game.toast) {
      this.toast.hidden = false;
      this.toast.classList.remove("hidden");
      this.toast.textContent = game.toast;
    } else {
      this.toast.hidden = true;
      this.toast.classList.add("hidden");
      this.toast.textContent = "";
    }

    if (game.phase === "lol" && game.lastClue) {
      this.magnet.hidden = false;
      this.magnet.classList.remove("hidden");
      this.magnet.textContent = `fridge · ${game.lastClue}`;
    } else {
      this.magnet.hidden = true;
      this.magnet.classList.add("hidden");
      this.magnet.textContent = "";
    }

    const judging = game.phase === "judge" && game.judgeId === this.room.selfId;
    this.felt.innerHTML = game.table
      .map((play) => {
        const face =
          game.phase === "play" ? backCard() : renderCard(play.card, judging ? play.id : undefined, play.id === game.winPlayId);
        return `<div class="table-card ${play.id === game.winPlayId ? "won" : ""}">${face}</div>`;
      })
      .join("");

    const mine = game.hands[this.room.selfId] ?? [];
    const canPlay = game.phase === "play" && game.judgeId !== this.room.selfId && !game.skipped.includes(this.room.selfId);
    this.hand.innerHTML = mine.map((card) => renderCard(card, canPlay ? card.id : undefined)).join("");
    this.hand.classList.toggle("dimmed", !canPlay);
    const handLabel = document.querySelector(".hand-label");
    if (handLabel) {
      handLabel.textContent = canPlay ? "Your punchlines — tap one" : game.judgeId === this.room.selfId ? "You’re picking. Hand can wait." : "Your cards";
    }

    this.deal.hidden = game.phase !== "lobby" && game.phase !== "over";
    this.deal.textContent = game.phase === "over" ? "One more round" : "Start anyway";
    this.next.hidden = game.phase !== "lol";

    if (game.phase === "lobby") this.hint.textContent = "Dealing you in…";
    else if (game.phase === "play" && game.judgeId === this.room.selfId)
      this.hint.textContent = "You’re the judge. Family is filling the blank.";
    else if (game.phase === "play") this.hint.textContent = "Tap a cream card. That’s your punchline.";
    else if (game.phase === "judge" && judging) this.hint.textContent = "Tap the funniest. You’re finishing the sentence.";
    else if (game.phase === "judge") this.hint.textContent = "Judge is picking the funniest…";
    else if (game.phase === "lol") this.hint.textContent = "That’s the joke. Read the yellow out loud.";
    else this.hint.textContent = "Night’s over. Peek at the fridge if you want.";

    this.dossier.innerHTML = this.renderFridge(state.members, state.fridge, game, this.room.selfId);

    this.thread.innerHTML = state.messages
      .map((msg) => {
        if (msg.kind === "system") return `<p class="day-stamp">${escapeHtml(msg.text)}</p>`;
        const member = state.members.find((m) => m.id === msg.from);
        const mineMsg = msg.from === this.room?.selfId;
        const name = msg.from === "hq" ? "HQ" : member?.name ?? "???";
        return `<article class="msg ${mineMsg ? "me" : "them"}">
          ${mineMsg ? "" : `<p class="who">${escapeHtml(name)}</p>`}
          <p class="bubble">${escapeHtml(msg.text)}</p>
        </article>`;
      })
      .join("");
    this.thread.scrollTop = this.thread.scrollHeight;
  }

  private renderFridge(members: Profile[], fridge: string[], game: GameState, selfId: string): string {
    const notes = fridge.map((item) => `<li>${escapeHtml(item)}</li>`).join("");
    const rows = members
      .filter((m) => !m.left)
      .map((m) => {
        const secret = game.secrets[m.id];
        const title =
          secret && (secret.exposed || m.id === selfId) ? (roleById(secret.roleId)?.title ?? "family") : "???";
        const clues = secret?.clues.map((c) => `<li>${escapeHtml(c)}</li>`).join("") ?? "";
        return `<div class="file">
          <p><strong>${escapeHtml(m.name)}</strong> · ${escapeHtml(title)}</p>
          <ul>${clues || "<li>no snitches yet</li>"}</ul>
        </div>`;
      })
      .join("");
    const optionsPeople = members
      .filter((m) => !m.left && m.id !== selfId)
      .map((m) => `<option value="${m.id}">${escapeHtml(m.name)}</option>`)
      .join("");
    const optionsRoles = ROLES.map((r) => `<option value="${r.id}">${escapeHtml(r.title)}</option>`).join("");
    return `<p class="sticky-kicker">The fridge</p>
      <p class="fridge-hint">Optional side quest. The cards are the game.</p>
      <ul class="fridge-notes">${notes}</ul>
      ${rows}
      <details class="guess-wrap">
        <summary>I know who they are</summary>
        <form class="guess">
          <select name="target">${optionsPeople}</select>
          <select name="role">${optionsRoles}</select>
          <button type="submit">On the fridge</button>
        </form>
      </details>`;
  }
}

function renderCard(card: PlayCard, playId?: string, winner = false): string {
  const bind = playId ? `data-card="${playId}" data-play="${playId}"` : "";
  const tag = playId ? "button" : "article";
  const win = winner ? "winner" : "";
  if (card.kind === "uno") {
    return `<${tag} class="card uno ${card.uno} ${win}" ${bind} type="button">
      <span class="uno-mark">${UNO_LABEL[card.uno]}</span>
      <span class="mark">UNO</span>
    </${tag}>`;
  }
  return `<${tag} class="card white ${win}" ${bind} type="button">
    <p>${escapeHtml(card.text)}</p>
    <span class="mark">HQ</span>
  </${tag}>`;
}

function backCard(): string {
  return `<article class="card back"><p>HQ</p><span class="mark">FAMILY</span></article>`;
}

function formatPrompt(text: string): string {
  return `<p>${escapeHtml(text).replaceAll("____", '<span class="blank">____</span>')}</p>`;
}

function formatPunchline(text: string, answer: string): string {
  const safe = escapeHtml(text);
  const bit = escapeHtml(answer);
  if (!bit) return `<p>${safe}</p>`;
  return `<p>${safe.replace(bit, `<span class="fill">${bit}</span>`)}</p>`;
}

function el(selector: string): HTMLElement {
  const node = document.querySelector<HTMLElement>(selector);
  if (!node) throw new Error(`Missing ${selector}`);
  return node;
}

function val(selector: string): string {
  return document.querySelector<HTMLInputElement>(selector)?.value ?? "";
}

function setVal(selector: string, value: string): void {
  const node = document.querySelector<HTMLInputElement>(selector);
  if (node) node.value = value;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}
